import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Check, Phone, MessageCircle, ChevronRight, BookMarked, Users, Sparkles, MapPin } from "lucide-react";
import * as Icons from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import SEO from "../components/SEO";
import ServiceCard from "../components/ServiceCard";
import { getServiceBySlug, getRelatedServices, services } from "../data/services";
import InlineLeadForm from "../components/InlineLeadForm";

const FIRM_PROVIDER = {
  "@type": "LegalService",
  name: "K & K Associates - Advocates & Solicitors",
  telephone: "+91-96435-72870",
  email: "info@kandkassociates.in",
  address: {
    "@type": "PostalAddress",
    streetAddress: "A-197, LGF, Defence Colony",
    addressLocality: "New Delhi",
    postalCode: "110024",
    addressRegion: "Delhi",
    addressCountry: "IN",
  },
  geo: { "@type": "GeoCoordinates", latitude: 28.5722, longitude: 77.2305 },
};

const AREA_SERVED = ["India", "Delhi NCR", "New Delhi", "Gurgaon", "Noida", "Faridabad", "Ghaziabad"];

// Build Service + FAQPage + BreadcrumbList JSON-LD for a service (kept out of the
// component to keep its render path simple).
function buildServiceSchema(service) {
  const graph = [
    {
      "@type": "Service",
      serviceType: service.title,
      provider: FIRM_PROVIDER,
      areaServed: AREA_SERVED,
      description: service.metaDescription,
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://kandkassociates.com/" },
        { "@type": "ListItem", position: 2, name: "Practice Areas", item: "https://kandkassociates.com/services" },
        { "@type": "ListItem", position: 3, name: service.title },
      ],
    },
  ];
  if (service.faqs?.length) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: service.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }
  return { "@context": "https://schema.org", "@graph": graph };
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  if (!service) return <Navigate to="/services" replace />;

  const Icon = Icons[service.icon] || Icons.Scale;
  const related = getRelatedServices(service.related || []);
  const combinedSchema = buildServiceSchema(service);
  const intro = service.deepIntro || (service.intro ? [service.intro] : []);

  return (
    <>
      <SEO
        title={service.title}
        description={service.metaDescription}
        keywords={[...(service.keywords || []), ...(service.localKeywords || [])]}
        canonical={`https://kandkassociates.com/services/${service.slug}`}
        schema={combinedSchema}
      />

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-14 md:pb-20 bg-bone border-b border-rule" data-testid={`service-hero-${service.slug}`}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <nav className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-ink/50 mb-10">
            <Link to="/" className="hover:text-brand">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/services" className="hover:text-brand">Practice Areas</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-brand truncate max-w-[60vw]">{service.title.split("—")[0]}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-9">
              <div className="flex items-center gap-4 mb-6">
                <Icon className="w-8 h-8 text-brand" strokeWidth={1.25} />
                <p className="kicker">Practice Area</p>
              </div>
              <h1 className="font-display text-4xl md:text-5xl xl:text-6xl leading-[1] text-ink">
                {service.title}
              </h1>
              <p className="mt-8 font-serif text-xl md:text-2xl text-ink/75 leading-relaxed max-w-3xl">
                {service.short}
              </p>
            </div>
            <div className="lg:col-span-3 flex flex-col gap-3">
              <Link to="/contact" className="btn-primary" data-testid="service-cta-consult">
                <Phone className="w-3.5 h-3.5" strokeWidth={1.5} /> Book Consultation
              </Link>
              <a
                href="https://wa.me/919643572870"
                target="_blank" rel="noreferrer"
                className="btn-outline"
                data-testid="service-cta-whatsapp"
              >
                <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-14 md:py-28 bg-paper">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Sticky TOC */}
          <aside className="lg:col-span-3 hidden lg:block">
            <div className="sticky top-32">
              <p className="kicker mb-6">On This Page</p>
              <ul className="space-y-3 text-sm font-sans text-ink/65">
                <li><a href="#overview" className="hover:text-brand link-underline">Overview</a></li>
                {service.sections.map((sec, i) => (
                  <li key={sec.heading}><a href={`#sec-${i}`} className="hover:text-brand link-underline">{sec.heading}</a></li>
                ))}
                {service.statutes && <li><a href="#statutes" className="hover:text-brand link-underline">Key Statutes</a></li>}
                {service.whoNeedsThis && <li><a href="#audience" className="hover:text-brand link-underline">Who Needs This</a></li>}
                {service.scenarios && <li><a href="#scenarios" className="hover:text-brand link-underline">Recent Outcomes</a></li>}
                {service.why && <li><a href="#why" className="hover:text-brand link-underline">Why K &amp; K</a></li>}
                {service.faqs && <li><a href="#faqs" className="hover:text-brand link-underline">FAQs</a></li>}
              </ul>
            </div>
          </aside>

          <article className="lg:col-span-9 font-serif text-lg md:text-xl text-ink/85 leading-[1.85]">
            <h2 id="overview" className="kicker !text-brand mb-4">Overview</h2>
            {intro.map((p, i) =>
              i === 0 ? <p key={p.slice(0, 40)} className="dropcap">{p}</p> : <p key={p.slice(0, 40)} className="mt-6">{p}</p>
            )}

            {service.sections.map((sec, i) => (
              <div key={sec.heading} id={`sec-${i}`} className="mt-16">
                <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight mb-6">{sec.heading}</h2>
                {sec.body && <p>{sec.body}</p>}
                {sec.bullets && (
                  <ul className="space-y-3 mt-6">
                    {sec.bullets.map((b) => (
                      <li key={b} className="flex gap-3 text-lg leading-relaxed">
                        <Check className="w-5 h-5 text-brand mt-1.5 shrink-0" strokeWidth={1.5} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}

            {service.statutes && (
              <div id="statutes" className="mt-20">
                <div className="flex items-center gap-3 mb-6">
                  <BookMarked className="w-6 h-6 text-brand" strokeWidth={1.25} />
                  <p className="kicker">Statutes & Regulations</p>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight mb-8">
                  Key laws governing this practice.
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-rule border border-rule">
                  {service.statutes.map((s) => (
                    <div key={s} className="bg-paper p-5 font-serif text-base text-ink/85">{s}</div>
                  ))}
                </div>
              </div>
            )}

            <InlineLeadForm
              source={`Service: ${service.title}`}
              service={service.title}
              heading={`Need help with ${service.title.split("—")[0].trim()}?`}
              subline="Leave your name and number — a partner will call you back within one business day for a free first consultation."
            />

            {service.whoNeedsThis && (
              <div id="audience" className="mt-20">
                <div className="flex items-center gap-3 mb-6">
                  <Users className="w-6 h-6 text-brand" strokeWidth={1.25} />
                  <p className="kicker">Who This Is For</p>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight mb-8">
                  Audiences we typically advise in this area.
                </h2>
                <ul className="space-y-3">
                  {service.whoNeedsThis.map((w) => (
                    <li key={w} className="flex gap-3 text-lg leading-relaxed">
                      <Check className="w-5 h-5 text-brand mt-1.5 shrink-0" strokeWidth={1.5} />
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {service.scenarios && (
              <div id="scenarios" className="mt-20">
                <div className="flex items-center gap-3 mb-6">
                  <Sparkles className="w-6 h-6 text-brand" strokeWidth={1.25} />
                  <p className="kicker">Recent Outcomes</p>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight mb-8">
                  Anonymised matter highlights.
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {service.scenarios.map((sc) => (
                    <div key={sc.title} className="border border-rule p-8 bg-bone">
                      <h3 className="font-display text-xl text-brand leading-tight mb-3">{sc.title}</h3>
                      <p className="font-serif text-base text-ink/80 leading-relaxed">{sc.body}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-6 text-xs font-sans text-ink/50 tracking-wide">Outcomes are matter-specific. Past results are not a guarantee of future performance.</p>
              </div>
            )}

            {service.why && (
              <div id="why" className="mt-20 p-10 md:p-14 bg-bone border-l-4 border-brand">
                <p className="kicker mb-5">Why K &amp; K</p>
                <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight mb-8">
                  Strategic differentiators in this practice.
                </h2>
                <ul className="space-y-4">
                  {service.why.map((w) => (
                    <li key={w} className="flex gap-3 text-lg">
                      <Check className="w-5 h-5 text-brand mt-1.5 shrink-0" strokeWidth={1.5} />
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {service.faqs && (
              <div id="faqs" className="mt-20">
                <p className="kicker mb-4">Frequently Asked</p>
                <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight mb-10">
                  Questions our clients ask first.
                </h2>
                <Accordion type="single" collapsible className="border-t border-rule">
                  {service.faqs.map((f, i) => (
                    <AccordionItem key={f.q} value={`faq-${i}`} className="border-b border-rule">
                      <AccordionTrigger className="text-left font-display text-xl md:text-2xl text-ink hover:no-underline py-6">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="font-serif text-lg text-ink/75 leading-relaxed pb-6">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}

            {/* GEO callout */}
            {service.localKeywords && (
              <div className="mt-20 border border-rule p-8 md:p-10 bg-bone">
                <div className="flex items-center gap-3 mb-4">
                  <MapPin className="w-5 h-5 text-brand" strokeWidth={1.5} />
                  <p className="kicker">Serving Delhi NCR & Beyond</p>
                </div>
                <p className="font-serif text-lg text-ink/80 leading-relaxed">
                  Our office at A-197, LGF, Defence Colony, New Delhi 110024 is the operating base for clients across Delhi NCR — Gurgaon, Noida, Faridabad, Ghaziabad — and pan-India through a vetted local-counsel network. We routinely appear at Saket, Patiala House, Tis Hazari, Karkardooma, Rohini and Dwarka District Courts; the Delhi High Court; and the Supreme Court of India.
                </p>
              </div>
            )}

            {/* CTA inside article */}
            <div className="mt-20 bg-ink text-white p-10 md:p-14">
              <p className="kicker text-white/60 mb-5">Begin a Conversation</p>
              <h3 className="font-display text-3xl md:text-4xl leading-tight">
                Discuss your <span className="italic text-white/70">{service.title.split("—")[0].trim().toLowerCase()}</span> matter with our partner desk.
              </h3>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/contact" className="btn-ghost-light" data-testid="service-bottom-cta-contact">
                  Book Consultation <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                </Link>
                <a href="tel:919643572870" className="btn-primary border-white" data-testid="service-bottom-cta-call">
                  <Phone className="w-3.5 h-3.5" strokeWidth={1.5} /> +91 96435 72870
                </a>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-bone border-t border-rule py-14 md:py-20">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <p className="kicker mb-5">Related Practice Areas</p>
            <h2 className="font-display text-3xl md:text-4xl text-ink mb-12">
              You may also be interested in.
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-rule">
              {related.map((r) => {
                const idx = services.findIndex((s) => s.slug === r.slug);
                return (
                  <div key={r.slug} className="border-r border-b border-rule">
                    <ServiceCard service={r} index={idx} />
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
